// IntensityChart/index.tsx
import React, { useRef, useState, useMemo } from 'react';
import { MenuProps, Spin } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
  TooltipProps,
} from 'recharts';
import GraphDropDown from 'views/Dashboard/COBarChart/GraphDropdown';
import { downloadGraph } from 'shared/utils/downloadStaticGraph';
import { DownloadKeys, DownloadLoader } from 'enums/GraphDropdownEnums';
import CustomTooltip from 'shared/components/CustomisedTooltip';
import { CustomTick } from './customTick';
import { formatValue } from 'shared/utils/formatWholeNum';
import styles from './intensityChart.module.scss';
import {
  CompanyDashboardProps,
  customStyleAnchor,
  customPosition,
  customPositionX,
  customFontTick,
  customMarginLineChart,
} from '../constants';
import { CurveType } from 'recharts/types/shape/Curve';

interface EmissionIntensityLineChartProps {
  data: { year: number; intensity: number }[];
  AxisProp: string;
  unit: string;
  height?: number;
  hasDownload?: boolean;
  loading?: boolean;
}

const EmissionIntensityLineChart: React.FC<EmissionIntensityLineChartProps> = ({
  data,
  AxisProp,
  unit,
  height = 350,
  hasDownload = true,
  loading,
}) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Calculate a "nice" number for y-axis max value to ensure evenly distributed ticks
  // But keep it closer to the actual max to avoid too much empty space
  const calculateNiceMax = (maxValue: number): number => {
    if (maxValue === 0) return 10; // Default to 10 if no data

    // Cap the increase to maximum 20% above the actual max value to avoid too much empty space
    const maxAllowed = maxValue * 1.2;

    // Find the order of magnitude (power of 10)
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxAllowed)));

    // Normalize the capped value to 0-10 range
    const normalized = maxAllowed / magnitude;

    // Round up to the next "nice" number with more flexibility
    // Allows numbers like 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10
    let niceNumber: number;
    if (normalized <= 1) {
      niceNumber = 1;
    } else if (normalized <= 2) {
      niceNumber = 2;
    } else if (normalized <= 3) {
      niceNumber = 3;
    } else if (normalized <= 4) {
      niceNumber = 4;
    } else if (normalized <= 5) {
      niceNumber = 5;
    } else if (normalized <= 6) {
      niceNumber = 6;
    } else if (normalized <= 7) {
      niceNumber = 7;
    } else if (normalized <= 8) {
      niceNumber = 8;
    } else if (normalized <= 9) {
      niceNumber = 9;
    } else {
      niceNumber = 10;
    }

    // Calculate the nice max value
    const niceMax = niceNumber * magnitude;

    // Ensure we don't go below the actual max value
    return Math.max(niceMax, maxValue);
  };

  const maxIntensity = useMemo(() => {
    return Math.max(...data.map((d) => d.intensity || 0), 0);
  }, [data]);

  const dynamicDomain = useMemo(() => {
    // Intensity values are already in correct units (tCO₂e per $MM for revenue, kgCO₂e per kg for weight)
    // No normalization needed - use values as-is
    const niceMax = calculateNiceMax(maxIntensity);
    return [0, niceMax];
  }, [maxIntensity]);

  // Generate evenly distributed tick values for the Y-axis
  const yAxisTicks = useMemo(() => {
    const domainMax = dynamicDomain[1];
    if (domainMax === 0) return [0];

    // Divide the range into 4 equal parts for nice distribution
    const tickCount = 4;
    const tickStep = domainMax / tickCount;

    // Find a nice step value
    const magnitude = Math.pow(10, Math.floor(Math.log10(tickStep)));
    const normalizedStep = tickStep / magnitude;

    // Round to nice step values (0.25, 0.5, 1, 2, 2.5, 5, etc.)
    let niceStepMultiplier: number;
    if (normalizedStep <= 0.25) {
      niceStepMultiplier = 0.25;
    } else if (normalizedStep <= 0.5) {
      niceStepMultiplier = 0.5;
    } else if (normalizedStep <= 1) {
      niceStepMultiplier = 1;
    } else if (normalizedStep <= 2) {
      niceStepMultiplier = 2;
    } else if (normalizedStep <= 2.5) {
      niceStepMultiplier = 2.5;
    } else if (normalizedStep <= 5) {
      niceStepMultiplier = 5;
    } else {
      niceStepMultiplier = 10;
    }

    const niceStep = niceStepMultiplier * magnitude;

    // Generate ticks: 0, step, 2*step, 3*step, ..., up to domainMax
    const ticks: number[] = [0];
    let currentTick = niceStep;
    while (currentTick <= domainMax) {
      ticks.push(currentTick);
      currentTick += niceStep;
    }

    // Ensure the last tick is at or above domainMax
    if (ticks[ticks.length - 1] < domainMax) {
      ticks.push(domainMax);
    }

    return ticks;
  }, [dynamicDomain]);

  const customPadding = useMemo(() => {
    return {
      left: data?.length === 1 ? 90 : 10,
      right: data?.length === 1 ? 400 : 0,
    };
  }, [data?.length]);

  const formatTick = (value: number) => {
    // Intensity values are already in correct units - display as-is
    return String(Math.round(value));
  };

  const renderTooltipContent = ({ payload }: TooltipProps<string, string>) => {
    if (!payload || !payload.length) return null;

    const point = payload[0].payload as { year: number; intensity: number };
    // Intensity values are already in correct units - use as-is
    const mapped = {
      ...point,
      totalCO2e: point.intensity,
    };

    return <CustomTooltip unit={unit} payload={mapped} />;
  };

  const handleDownload: MenuProps['onClick'] = async ({ key }) => {
    if (key === DownloadKeys.CSV) {
      const rows = [
        ['Year', `Emission Intensity (${unit})`],
        ...data.map((d) => [String(d.year), String(d.intensity ?? 0)]),
      ];
      const csv = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${CompanyDashboardProps.INTENSITY_CHART}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    await downloadGraph(
      key,
      graphRef,
      CompanyDashboardProps.INTENSITY_CHART,
      setIsDownloading
    );
  };

  const isDataEmpty = !data || data.length === 0;

  return (
    <>
      {isDataEmpty ? (
        <p className={styles.emptyText}>
          {CompanyDashboardProps.DATA_EMPTY}
        </p>
      ) : (
        <Spin
          spinning={isDownloading || !!loading}
          {...(isDownloading && { tip: DownloadLoader.DOWNLOADING })}
        >
          <div className={styles.wrapper}>
            <div className={styles.positionedDiv}>
              <GraphDropDown
                handleDownload={handleDownload}
                showSort={false}
                isDownload={hasDownload}
                isMainDashboard
                handleSort={() => { }}
                graphRef={graphRef}
                setIsDownloading={setIsDownloading}
              />
            </div>

            <div ref={graphRef}>
              <ResponsiveContainer width="100%" height={height}>
                <LineChart data={data} margin={customMarginLineChart}>
                  <CartesianGrid strokeDasharray="5 5" horizontal vertical={false} />

                  <XAxis
                    dataKey="year"
                    angle={-45}
                    dy={10}
                    dx={-10}
                    tick={<CustomTick x={20} y={0} payload={{ value: '' }} />}
                    padding={customPadding}
                  >
                    <Label
                      value={AxisProp}
                      position={customPositionX}
                      offset={-24}
                      style={customStyleAnchor}
                    />
                  </XAxis>

                  <YAxis
                    domain={dynamicDomain}
                    ticks={yAxisTicks}
                    tick={customFontTick}
                    axisLine={false}
                    tickFormatter={formatTick}
                    tickLine={false}
                  >
                    <Label
                      value={`Emission Intensity (${unit})`}
                      position={customPosition}
                      angle={-90}
                      offset={-10}
                      style={customStyleAnchor}
                    />
                  </YAxis>

                  <Line
                    type={CompanyDashboardProps.Linear as CurveType}
                    dataKey="intensity"
                    stroke={CompanyDashboardProps.LINE_STROKE}
                    strokeWidth={CompanyDashboardProps.LINE_STROKE_WIDTH}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    label={({ x, y, value }) => {
                      // Intensity values are already in correct units - display as-is
                      return (
                        <text
                          x={x}
                          y={y}
                          dy={-16}
                          dx={3}
                          fill={CompanyDashboardProps.LINE_FILL}
                          fontSize={12}
                          style={customStyleAnchor}
                        >
                          {formatValue(value)}
                        </text>
                      );
                    }}
                  />

                  <Tooltip cursor={false} content={renderTooltipContent} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Spin>
      )}
    </>
  );
};

export default EmissionIntensityLineChart;

